// ENG-7798: persist the kanban-nudge backoff state across manager restarts.
//
// The manager keeps an in-memory Map of `code_name -> {signature, nudgedAt,
// nudgeCount}` (kanbanNudgeStateByCode) so an unchanged actionable board
// backs off its re-nudge cadence (ENG-7704, escalated by ENG-7798). That Map
// lived at module scope only, so every manager restart / CLI self-update
// reset each blocked agent to the fast cadence and restarted the escalation
// ladder from the bottom - the same wipe-on-restart pathology as
// ENG-7771/ENG-7779, in a lower-stakes form.
//
// Unlike the sender-policy sidecar there is no fire-time subtlety here: an
// entry records a nudge that was already DELIVERED (a fact at write time),
// and the failure direction of a lost/corrupt file is one fast-cadence
// nudge round, never a dropped delivery. Follows the channel-hash-cache
// pattern (load at boot, save on mutation, silent degradation) with a
// version field so a future state-shape change discards stale files
// wholesale.

import { existsSync, readFileSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';

import type { KanbanNudgeBackoffState } from './parsers.js';

const CACHE_FILENAME = 'kanban-nudge-state.json';

/** Bump when the KanbanNudgeBackoffState shape/semantics change. */
export const KANBAN_NUDGE_STATE_VERSION = 1;

export function getKanbanNudgeStateFile(configDir: string): string {
  return join(configDir, CACHE_FILENAME);
}

/**
 * Hydrate `target` (code_name -> backoff state) with persisted entries.
 * Missing / unreadable / malformed / version-mismatched files are treated as
 * empty - the cost is one fast-cadence nudge round per blocked agent, never
 * a correctness break.
 */
export function loadKanbanNudgeState(
  target: Map<string, KanbanNudgeBackoffState>,
  configDir: string,
): void {
  const path = getKanbanNudgeStateFile(configDir);
  if (!existsSync(path)) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(path, 'utf-8'));
  } catch {
    return;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
  const obj = parsed as Record<string, unknown>;
  if (obj.version !== KANBAN_NUDGE_STATE_VERSION) return;
  const entries = obj.agents;
  if (!entries || typeof entries !== 'object' || Array.isArray(entries)) return;
  for (const [codeName, value] of Object.entries(entries as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue;
    const v = value as Record<string, unknown>;
    // Non-finite guard (CodeRabbit PR #3441): valid JSON like 1e400 parses to
    // Infinity, and `now - Infinity` never reaches the cadence - that would
    // permanently suppress the signature instead of degrading safely.
    if (
      typeof v.signature !== 'string' ||
      typeof v.nudgedAt !== 'number' ||
      !Number.isFinite(v.nudgedAt)
    ) continue;
    // ENG-6876: failureCount/failedAt must survive a restart too. This loader
    // FIELD-PICKS rather than spreading, so a new field is silently dropped
    // unless it is named here — and dropping these would reset the delivery
    // backoff to zero on every manager restart, which is the same
    // wipe-on-restart pathology described in this file's header. A failing rail
    // also correlates with restarts, so the reset would land exactly when the
    // backoff matters most.
    //
    // Deliberately NOT bumping KANBAN_NUDGE_STATE_VERSION: these fields are
    // additive and optional, so a v1 file written before them loads cleanly as
    // "no failures". Bumping would discard every existing file wholesale and
    // reset every agent's escalation ladder to the bottom — a real cost to buy
    // nothing.
    const failureCount =
      typeof v.failureCount === 'number' && Number.isFinite(v.failureCount) && v.failureCount >= 1
        ? Math.floor(v.failureCount)
        : undefined;
    const failedAt =
      typeof v.failedAt === 'number' && Number.isFinite(v.failedAt) ? v.failedAt : undefined;
    target.set(codeName, {
      signature: v.signature,
      nudgedAt: v.nudgedAt,
      nudgeCount:
        typeof v.nudgeCount === 'number' && Number.isFinite(v.nudgeCount) && v.nudgeCount >= 1
          ? Math.floor(v.nudgeCount)
          : 1,
      // Only carry the pair when the count is real: a failedAt with no count
      // cannot gate anything, and a count with no timestamp is handled as
      // "due now" by shouldAttemptNoticeEnqueue rather than wedging the agent.
      ...(failureCount !== undefined ? { failureCount } : {}),
      ...(failureCount !== undefined && failedAt !== undefined ? { failedAt } : {}),
    });
  }
}

/**
 * Persist `source` to disk. Failures are non-fatal - the state stays in
 * memory and the next save may succeed (worst case: one fast-cadence nudge
 * round after the next manager restart).
 */
export function saveKanbanNudgeState(
  source: Map<string, KanbanNudgeBackoffState>,
  configDir: string,
): void {
  const path = getKanbanNudgeStateFile(configDir);
  const agents: Record<string, KanbanNudgeBackoffState> = {};
  for (const [codeName, state] of source) agents[codeName] = state;
  try {
    writeFileSync(path, JSON.stringify({ version: KANBAN_NUDGE_STATE_VERSION, agents }, null, 2));
  } catch {
    // intentional no-op; see doc comment
  }
}
